import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import { PageHeader } from "@ant-design/pro-components";
import {
  Layout,
  Breadcrumb,
  Row,
  Col,
  Empty,
  Input,
  Tag,
  Spin,
  notification,
  Button,
  Pagination,
} from "antd";
import { SearchOutlined, PlusCircleOutlined } from "@ant-design/icons";

/**
 * Components
 */
import TopBar from "components/NavBar/TopBar";
import DrawerSider from "components/NavBar/DrawerSider";
import { LayoutTopSide } from "components/NavBar/styles";


/**
 * Actions
 */
import {
  allCategoriesWithObj,
  allCategoriesObj,
  addHitByObj,
} from "auth/actions/objActions";
import PermissionWrapperComponent from "../../../components/PermissionWrapperComponent";
import { checkPermissionsFromUser, getAllPermissionsByUnit } from "../../../utils/permission";
import { contrastText } from "../../../utils/color";
import FormRow from "./FormRow";
import { clearFormSummaryCache } from "./useFormSummary";
import { defaultTablePagination, DEFAULT_PAGE_SIZE } from "../../../utils/table-pagination";
import "./styles.scss";

/**
 * Misc
 */
const { Content } = Layout;
const { CheckableTag } = Tag;

/**
 * Array para local storage / query param
 */
const NAME_FILTERS_LOCAL_STORAGE = {
  SEARCH: 'searchSelected',
  CATEGORY: 'categorySelected'
};
const NAME_QUERY_PARAMS = {
  SEARCH: 'search',
  CATEGORY: 'category'
};

// Cor de apoio para categorias sem cor cadastrada
const ACCENT_COLOR = "#0F766E";

// Comparação sem acentos e sem caixa, para a busca funcionar com "PRODUCAO" ou "produção"
// (faixa U+0300-U+036F: sinais diacríticos separados pelo normalize NFD)
const COMBINING_MARKS_REGEX = new RegExp(
  "[" + String.fromCharCode(0x0300) + "-" + String.fromCharCode(0x036f) + "]",
  "g"
);
const normalize = (text = '') =>
  text.toString().normalize('NFD').replace(COMBINING_MARKS_REGEX, '').toLowerCase();

function AllForms({ user }) {
  const permissions = useSelector((state) => state.user.permissions);
  const company = useSelector((state) => state.user.company);
  const navigate = useNavigate();
  const location = useLocation();
  const [forms, setForms] = useState([]);
  const [categoryColors, setCategoryColors] = useState({});
  const [permissionEntity, setPermissionEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  //
  const urlParams = new URLSearchParams(location.search);
  const initialSearch = urlParams.get(NAME_QUERY_PARAMS.SEARCH) || localStorage.getItem(NAME_FILTERS_LOCAL_STORAGE.SEARCH) || "";
  const initialCategories = (urlParams.get(NAME_QUERY_PARAMS.CATEGORY) || localStorage.getItem(NAME_FILTERS_LOCAL_STORAGE.CATEGORY) || "")
    .split(",")
    .filter(Boolean);
  const [searchText, setSearchText] = useState(initialSearch);
  const [selectedCategories, setSelectedCategories] = useState(initialCategories);

  // UseEffect responsável por incluir os filtros persistentes via query param/localStorage
  useEffect(() => {
    const newUrlParams = new URLSearchParams(location.search);

    if (searchText) {
      newUrlParams.set(NAME_QUERY_PARAMS.SEARCH, searchText);
      localStorage.setItem(NAME_FILTERS_LOCAL_STORAGE.SEARCH, searchText);
    } else {
      newUrlParams.delete(NAME_QUERY_PARAMS.SEARCH);
      localStorage.removeItem(NAME_FILTERS_LOCAL_STORAGE.SEARCH);
    }

    if (selectedCategories.length > 0) {
      newUrlParams.set(NAME_QUERY_PARAMS.CATEGORY, selectedCategories.join(","));
      localStorage.setItem(NAME_FILTERS_LOCAL_STORAGE.CATEGORY, selectedCategories.join(","));
    } else {
      newUrlParams.delete(NAME_QUERY_PARAMS.CATEGORY);
      localStorage.removeItem(NAME_FILTERS_LOCAL_STORAGE.CATEGORY);
    }

    if (newUrlParams.toString !== location.search.substring(1)) {
      navigate({
        pathname: location.pathname,
        search: newUrlParams.toString(),
      });
    }

  }, [searchText, selectedCategories, navigate, location.pathname, location.search])

  const newForm = () => {
    navigate({
      pathname: `/formularios/novo`,
    });
  };

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      // Busca as cores configuradas nas categorias; sem elas a listagem segue no padrão
      allCategoriesObj({ companyId: company.companyId, unitId: company.unitId })
        .then((items) => {
          const colors = {};
          (items.message || []).forEach((category) => {
            if (category.color && category.color.hex) {
              colors[normalize(category.name)] = category.color.hex;
            }
          });
          setCategoryColors(colors);
        })
        .catch(() => {});

      allCategoriesWithObj(company.unitId)
        .then((items) => {
          const permissionEntityData = getAllPermissionsByUnit(permissions, company.unitId, [
            "EDIT_FORM",
            "DELETE_FORM",
            "CREATE_FIELD_IN_FORM",
            "EDIT_FIELD_IN_FORM",
            "DELETE_FIELD_IN_FORM",
            "ORDER_FIELDS_IN_FORM",
            "INSERT_DATA_IN_FORM",
            "EDIT_DATA_IN_FORM",
            "DELETE_DATA_IN_FORM",
            "VIEW_DATA_FROM_FORM",
            "IMPORT_DATA_IN_FORM",
            "VIEW_DATA_ARCHIVED_FROM_FORM"
          ]);

          setPermissionEntity(permissionEntityData);
          const permissionEntityById = permissionEntityData
            .filter(item => item.context)
            .map(item => item.context.entity_id)
          const results = items.message
            .filter(item => permissionEntityById.includes(item._id))
            .map((values) => ({
              _id: values._id,
              name: values.name,
              shortName: values.name.replace("obj_", ""),
              // Descrição só aparece se o cadastro do formulário tiver uma
              description: values.description || "",
              categoriesList: values.categories || [],
            }));
          // A listagem foi remontada: os números em cache podem ter envelhecido
          clearFormSummaryCache();
          setForms(results);
        })
        .catch((err) => {
          notification["error"]({
            message: "Error",
            description: err.message
              ? err.message
              : "Something went wrong, please try again!",
          });
        })
        .finally(() => setLoading(false));
    }
  }, [user, location]);

  const isSearching = searchText.trim().length > 0;

  // Cada formulário carrega as próprias categorias já resolvidas em cor: a
  // primeira delas dá a fita da linha, as demais aparecem como tag
  const decoratedForms = useMemo(
    () =>
      forms.map((form) => {
        const names = form.categoriesList.length > 0 ? form.categoriesList : ["Sem categoria"];
        return {
          ...form,
          categories: names.map((name) => ({
            name,
            color: categoryColors[normalize(name)] || ACCENT_COLOR,
          })),
        };
      }),
    [forms, categoryColors]
  );

  // Lista plana em ordem alfabética — a categoria vira coluna, não seção
  const processedForms = useMemo(() => {
    const query = normalize(searchText.trim());

    return decoratedForms
      .filter(
        (form) =>
          selectedCategories.length === 0 ||
          form.categories.some((category) => selectedCategories.includes(category.name))
      )
      .filter(
        (form) =>
          !query ||
          normalize(form.shortName).includes(query) ||
          // Buscar pelo nome da categoria também traz os formulários dela
          form.categories.some((category) => normalize(category.name).includes(query))
      )
      .sort((a, b) => a.shortName.localeCompare(b.shortName, "pt-BR", { sensitivity: "base" }));
  }, [decoratedForms, searchText, selectedCategories]);

  const totalForms = processedForms.length;

  const categoryNames = useMemo(() => {
    const names = new Set();
    decoratedForms.forEach((form) =>
      form.categories.forEach((category) => names.add(category.name))
    );
    return [...names].sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }));
  }, [decoratedForms]);

  // Filtro ou busca novos: voltar para a primeira página, senão o usuário pode
  // cair numa página que não existe mais no resultado
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedCategories]);

  const pagedForms = useMemo(
    () => processedForms.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [processedForms, currentPage, pageSize]
  );

  const toggleCategoryFilter = (categoryName) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryName)
        ? prev.filter((name) => name !== categoryName)
        : [...prev, categoryName]
    );
  };

  const clearCategoryFilter = () => {
    setSelectedCategories([]);
  };

  const routes = [
    {
      breadcrumbName: "Formulários",
    },
    {
      breadcrumbName: "todos",
    },
  ];

  const pushHit = ({ id, index, name, route }) => {
    addHitByObj({
      id: id,
      index: index,
    })
      .then(async (response) => {
        const value = encodeURIComponent(name);
        navigate(`/formularios/${route}/${value}`);
      })
      .catch((err) =>
        notification["error"]({
          message: "Erro",
          description: err.message
            ? err.message
            : "Algo está errado, tente novamente!",
        })
      );
  };

  const renderFormRow = (form) => {
    // A fita vertical usa a cor da primeira categoria — é a única marca de cor
    // forte que sobra na linha
    const rowAccent = form.categories[0].color;
    const formPermissions = permissionEntity.filter(
      (item) => item.context && item.context.entity_id === form._id
    );
    const canConfig = checkPermissionsFromUser(formPermissions, [
      'DELETE_FORM',
      'CREATE_FIELD_IN_FORM',
      'EDIT_FIELD_IN_FORM',
      'DELETE_FIELD_IN_FORM',
      'ORDER_FIELDS_IN_FORM',
      'INSERT_DATA_IN_FORM',
      'EDIT_DATA_IN_FORM',
      'IMPORT_DATA_IN_FORM',
      'VIEW_DATA_ARCHIVED_FROM_FORM'
    ], company.unitId, 'form', form._id);
    const canRecords = checkPermissionsFromUser(formPermissions, [
      'INSERT_DATA_IN_FORM',
      'EDIT_DATA_IN_FORM',
      'VIEW_DATA_FROM_FORM',
      'VIEW_LOG_FROM_FORM'
    ], company.unitId, 'form', form._id);

    return (
      <FormRow
        key={form._id}
        form={form}
        accent={rowAccent}
        canConfig={canConfig}
        canRecords={canRecords}
        onOpen={(route) =>
          pushHit({
            id: form._id,
            index: "forms",
            name: form.name,
            route,
          })
        }
      />
    );
  };

  return (
    <LayoutTopSide>
      <TopBar />
      <Layout style={{ overflow: "hidden" }}>
        <DrawerSider />
        <Layout style={{ padding: "0 24px 24px", height: "100%", overflowY: "auto" }}>
          <PageHeader
            style={{ margin: "16px 0 5px 0" }}
            title="Lista de formulários"
            subTitle="Utilize a biblioteca de formulários para administrar novos campos e funções."
            breadcrumb={
              <Breadcrumb>
                {routes.map((a) =>
                  a.hasOwnProperty("href") ? (
                    <Breadcrumb.Item key={Math.random()}>
                      <a href onClick={() => navigate(a.href)}>
                        {a.breadcrumbName}
                      </a>
                    </Breadcrumb.Item>
                  ) : (
                    <Breadcrumb.Item>{a.breadcrumbName}</Breadcrumb.Item>
                  )
                )}
              </Breadcrumb>
            }
          />
          <Content
            className="site-layout-background"
            style={{
              padding: 24,
              margin: 0,
              minHeight: "fit-content",
              background: "var(--cp-surface)",
            }}
          >
            <Row gutter={[16, 8]}>
              <Col xs={24} md={16} lg={18}>
                <Input
                  placeholder="Buscar formulário ou categoria"
                  prefix={<SearchOutlined style={{ color: ACCENT_COLOR }} />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                  style={{ borderRadius: 12 }}
                />
              </Col>
              <Col xs={24} md={8} lg={6}>
                <PermissionWrapperComponent permissions={['CREATE_FORM']}>
                  <Button
                    type="primary"
                    onClick={newForm}
                    style={{ width: "100%" }}
                  >
                    Adicionar Formulário
                    <PlusCircleOutlined />
                  </Button>
                </PermissionWrapperComponent>
              </Col>
            </Row>
            <div
              style={{
                display: "flex",
                gap: 8,
                overflowX: "auto",
                padding: "12px 0 4px 0",
              }}
            >
              <CheckableTag
                className="forms-chip"
                checked={selectedCategories.length === 0}
                onChange={clearCategoryFilter}
                style={
                  selectedCategories.length === 0
                    ? { backgroundColor: ACCENT_COLOR, borderColor: ACCENT_COLOR, color: "#FFFFFF" }
                    : undefined
                }
              >
                Todas
              </CheckableTag>
              {categoryNames.map((name) => {
                const chipColor = categoryColors[normalize(name)];
                const isChecked = selectedCategories.includes(name);

                return (
                  <CheckableTag
                    className="forms-chip"
                    key={name}
                    checked={isChecked}
                    onChange={() => toggleCategoryFilter(name)}
                    // Só o chip selecionado recebe a cor cheia. Nos demais a
                    // categoria é identificada por um ponto, para a régua de
                    // filtros não virar uma faixa de cores saturadas
                    style={
                      isChecked
                        ? {
                          backgroundColor: chipColor || ACCENT_COLOR,
                          borderColor: chipColor || ACCENT_COLOR,
                          color: contrastText(chipColor || ACCENT_COLOR),
                        }
                        : undefined
                    }
                  >
                    {chipColor && !isChecked && (
                      <span className="forms-chip__dot" style={{ backgroundColor: chipColor }} />
                    )}
                    {name}
                  </CheckableTag>
                );
              })}
            </div>
            {(isSearching || selectedCategories.length > 0) && (
              <div
                style={{
                  fontSize: 13,
                  color: ACCENT_COLOR,
                  fontWeight: 600,
                  marginTop: 4,
                }}
              >
                {totalForms} formulário{totalForms !== 1 ? "s" : ""} encontrado{totalForms !== 1 ? "s" : ""}
              </div>
            )}
            {loading ? (
              <div style={{ textAlign: "center", padding: 60 }}>
                <Spin size="large" />
                <div style={{ marginTop: 12, color: ACCENT_COLOR }}>
                  Carregando formulários...
                </div>
              </div>
            ) : processedForms.length === 0 ? (
              <Empty
                style={{ padding: 40 }}
                description={
                  forms.length === 0 ? (
                    "Nenhum formulário adicionado."
                  ) : (
                    <>
                      <div style={{ fontWeight: 600 }}>Nenhum formulário encontrado</div>
                      <div style={{ color: "#888", marginTop: 4 }}>
                        Tente ajustar a busca ou os filtros de categoria
                      </div>
                    </>
                  )
                }
              />
            ) : (
              <div className="forms-list">
                <div className="forms-list__row forms-list__head" aria-hidden="true">
                  <span>Formulário</span>
                  <span className="forms-list__col--category">Categoria</span>
                  <span className="forms-list__col--records">Registros</span>
                  <span className="forms-list__col--actions">Ações</span>
                </div>
                {pagedForms.map(renderFormRow)}
                <div className="forms-list__footer">
                  <Pagination
                    {...defaultTablePagination}
                    current={currentPage}
                    pageSize={pageSize}
                    total={totalForms}
                    onChange={(page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    }}
                  />
                </div>
              </div>
            )}
          </Content>
        </Layout>
      </Layout>
    </LayoutTopSide>
  );
}

const mapStateToProps = ({ session }) => ({
  user: session.user,
});

export default connect(mapStateToProps)(AllForms);
